import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { StorageService } from 'app/core/services/storage/storage.service';
import * as _ from 'lodash';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { filter, first, map, tap, withLatestFrom } from 'rxjs/operators';
import { ClientInfo } from './core/services/benutzerprofil/models/clientInfo';
import { GlobalService } from './core/services/global/global.service';
import { I18nService } from './core/services/i18n/i18n.service';
import { IViews } from './modules/shared-area/ducks/panel/models/IViews';
import { getNamedViews } from './modules/shared-area/ducks/panel/store/panel.selectors';
import { UserProfileEffects } from './modules/shared-area/ducks/user-profile/store/user-profile.effects';
import { getUserProfileApiState } from './modules/shared-area/ducks/user-profile/store/user-profile.selectors';
import { UserActions } from './modules/shared-area/ducks/user/store';
import { IAppState } from './modules/shared-area/root-state';
import { IntlService } from '@progress/kendo-angular-intl';
import { IView } from 'app/modules/shared-area/ducks/panel/models/IView';
import { environment } from '@environments/environment';
import { Location } from '@angular/common';
import { GenericTableTranslationService } from './shared/components/generic-table/generic-table-translation.service';
import { TranslateService } from '@ngx-translate/core';
import { GenericUploadTranslationService } from './shared/components/generic-upload';
import { ComboboxTranslationService } from './shared/directives/combobox';
import { GenericItemListTranslationService } from './shared/components/generic-item-list/generic-item-list-translation.service';
import { GenericInlineItemListTranslationService } from './shared/components/generic-inline-item-list/generic-inline-item-list-translation.service';
import { GenericTableListTranslationService } from './shared/components/generic-table-list/generic-table-list-translation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy, OnInit {
  title = 'app';
  private namedViews$: Observable<IViews> = EMPTY;
  private userProfileIsLoaded$: Observable<Boolean> = EMPTY;
  private userIsLoaded$: Observable<Boolean> = EMPTY;
  private subscriptions: Subscription[] = [];

  public get Initialized(): boolean {
    return this._initialized;
  }

  private subscriptionToggle: Subscription;
  private onAuthorizationResult: Subscription;
  private onModuleSetup: Subscription;
  private getIsAuthorized: Subscription;
  private _initialized = false;

  public maintenance$: Observable<any>;

  constructor(
    public globalService: GlobalService,
    private i18n: I18nService,
    public oidcSecurityService: OidcSecurityService,
    private storageService: StorageService,
    private router: Router,
    private userProfileEffects: UserProfileEffects,
    private store: Store<IAppState>,
    private intlService: IntlService,
    private location: Location,
    private genericTableTranslationService: GenericTableTranslationService,
    private genericUploadTranslationService: GenericUploadTranslationService,
    private comboboxTranslationService: ComboboxTranslationService,
    private genericTableListTranslationService: GenericTableListTranslationService,
    private genericInlineItemListTranslationService: GenericInlineItemListTranslationService,
    private genericItemListTranslationService: GenericItemListTranslationService,
    private translateService: TranslateService,
    private elRef: ElementRef
  ) {
    this.maintenance$ = globalService.maintenance.asObservable();

    this.handleSaveClientInfo();

    this.userProfileIsLoaded$ = this.store.pipe(
      select(getUserProfileApiState),
      map(apistate => apistate === 'Success'),
    );

    this.getIsAuthorized = this.oidcSecurityService.isAuthenticated$
      .pipe(
        filter(authResult => authResult.isAuthenticated),
        withLatestFrom(this.userProfileIsLoaded$),
        first(),
        tap(([authResult, isLoaded]) => {
          if (!isLoaded) {
            this.store.dispatch(UserActions.loadUser());
            this.userProfileEffects.loadUserProfilesAsync();
          }
        }),
      )
      .subscribe();

    this.userIsLoaded$ = this.store.pipe(
      select(state => state.shared.user.userData),
      filter(user => !!user),
      map(user => !!user),
    );

    this.subscriptionToggle = this.globalService.event.subscribe(event => {
      // this.toggle = event;
    });
    this.initAndLoadLanguage();
    this.elRef.nativeElement.setAttribute('app-version', environment.version);
  }

  ngOnInit(): void {
    this.onAuthorizationResult = this.oidcSecurityService.checkAuth().subscribe(
      authResult => this.onAuthorizationResultComplete(authResult.isAuthenticated),
      () => this.oidcSecurityService.logoff().subscribe({
        next: () => {
          console.log('Logoff initiated');
        },
        error: (err) => {
          console.error('Logoff error', err);
        }
      }),
    );


    this.subscriptions.push(
      this.translateService.onLangChange.subscribe(e => {
        const code = e.lang.substring(0, 2);
        this.genericTableTranslationService.setLanguage(code);
        this.genericUploadTranslationService.setLanguage(code);
        this.comboboxTranslationService.setLanguage(code);
        this.genericTableListTranslationService.setLanguage(code);
        this.genericInlineItemListTranslationService.setLanguage(code);
        this.genericItemListTranslationService.setLanguage(code);
      })
    );
  }

  public handleSaveClientInfo() {
    this.namedViews$ = this.store.pipe(select(getNamedViews));
    const apiState$ = this.store.pipe(select(getUserProfileApiState));
    this.subscriptions.push(
      this.namedViews$
        .pipe(
          withLatestFrom(apiState$),
          filter(([views, apiState]) => apiState === 'Success'),
          tap(([views, apiState]) => {
            const clientInfo = new ClientInfo();
            const userViews = _.omitBy(views, (view: { view: IView }) => view.view.isSystem);
            clientInfo.namedViews = userViews;
            this.userProfileEffects.saveClientInfoAsync(clientInfo);
          }),
        )
        .subscribe(),
    );
  }

  initAndLoadLanguage() {
    this.i18n.init();
    this.userIsLoaded$
      .pipe(
        first(),
        tap(() => {
          console.log('UBA Cheminfo Version: ' + environment.version);
          this._initialized = true;
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.subscriptionToggle.unsubscribe();
    if (this.onModuleSetup != null) {
      this.onModuleSetup.unsubscribe();
    }
    this.onAuthorizationResult.unsubscribe();
    this.getIsAuthorized.unsubscribe();

    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  private onAuthorizationResultComplete(isAuthenticated: boolean) {
    if (isAuthenticated) {
      const returnUrl: string = this.storageService.getItem('returnUrl');
      if (!_.isNil(returnUrl)) {
        this.storageService.removeItem('returnUrl');
        setTimeout(() => this.router.navigateByUrl(returnUrl), 0);
      }
      return;
    }
    // silent renew failed (session cookie is timed out)
    this.storageService.setItem('returnUrl', this.location.path());
    this.router.navigate(['/login']);
  }

  formatDate(date: any): string {
    return this.intlService.formatDate(this.intlService.parseDate(date), 'g');
  }

  public signOut() {
    this.oidcSecurityService.logoff().subscribe({
      next: () => {
        console.log('Logoff initiated');
      },
      error: (err) => {
        console.error('Logoff error', err);
      }
    });
  }
}
